import { Fragment, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useForm } from 'react-hook-form'
import isEmpty from 'lodash/isEmpty'
import {
  Input,
  Button,
  Heading,
  Checkbox,
  Select,
  Radio,
  Stack,
  Row,
  Col,
  Text
} from '@leroy-merlin-br/backyard-react-ui'

import * as validator from 'reactUtils/validators/validators'
import * as dateHelper from 'reactUtils/date-helpers/date-helpers'
import { formatters } from 'reactUtils/formatters'

import helperMessages from 'lmcv/utils/helperMessages'

import * as S from './styled'

const Form = ({
  initialState,
  regions = [],
  stores = [],
  occupations = [],
  onRegionChange,
  onSubmit,
  loading,
  isLoyaltyPro
}) => {
  const {
    birthDate,
    favoriteStore,
    preferences: { loyaltyProgram },
    regionSlug
  } = initialState

  const { handleSubmit, register, errors, setValue } = useForm({
    mode: 'onChange',
    defaultValues: {
      birthDate: formatters.birthDate(birthDate),
      favoriteStore,
      loyaltyProgram,
      region: regionSlug
    }
  })

  const [buttonDisabled, setButtonDisabled] = useState(false)
  const [hasFavoriteStore, setHasFavoriteStore] = useState(true)
  const [isEmployee, setEmployee] = useState(false)

  const toggleCheckbox = event => setButtonDisabled(event)

  const handleRegion = event => onRegionChange(event.target.value)

  const formatDate = target => {
    target.value = target.value
      .replace(/\D/g, '')
      .replace(/(\d{2})(\d{1,2})/, '$1/$2')
      .replace(/(\d{2})\/(\d{2})(\d{1,4})/, '$1/$2/$3')
  }

  const buildOptionsCamelCase = (data, dataType) => (
    <Fragment>
      {data.map((item, index) => (
        <option key={`${item.region}-${index}`} value={item[dataType]}>
          {item[`${dataType}Name`]}
        </option>
      ))}
    </Fragment>
  )

  const buildOptionsByKey = data => (
    <Fragment>
      {data.map(item => (
        <option key={`${item.id}`} value={item.id}>
          {item.name}
        </option>
      ))}
    </Fragment>
  )

  const toggleEmployeeLoyalty = () => {
    setEmployee(!isEmployee)
  }

  useEffect(() => {
    if (stores.length && hasFavoriteStore) {
      setValue('preferences[favoriteStore]', favoriteStore)
      setHasFavoriteStore(false)
    }
  }, [stores, hasFavoriteStore, favoriteStore, setValue])

  const birthdateRangeValidation = (value) => {
    return validator.isBirthdate(value) && dateHelper.isAcceptedAge(value)
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing="mega">
        <Input
          type="text"
          name="birthDate"
          id="birthDate"
          maxLength={10}
          onKeyUp={({ target }) => formatDate(target)}
          ref={register({
            required: true,
            pattern: /\d{2}[/]\d{2}[/]\d{4}/,
            maxLength: 10,
            validate: value => birthdateRangeValidation(value)
          })}
          isInvalid={!!errors.birthDate}
          placeholder="Data de nascimento"
          validationHint={
            !isEmpty(errors.birthDate) && helperMessages('birthDate', errors)
          }
        />

        <Row>
          <Col size={{ giga: 6 }}>
            <Select
              id="region"
              name="region"
              ref={register({ required: true })}
              isInvalid={!!errors.region}
              onChange={handleRegion}
              placeholder="Escolha uma região"
              validationHint={
                !isEmpty(errors.region) && helperMessages('region', errors)
              }
            >
              {buildOptionsCamelCase(regions, 'region')}
            </Select>
          </Col>
          <Col size={{ giga: 6 }}>
            <Select
              id="preferences[favoriteStore]"
              name="preferences[favoriteStore]"
              ref={register({ required: true })}
              isInvalid={!!errors.preferences?.favoriteStore}
              placeholder="Escolha uma loja favorita"
              validationHint={
                !isEmpty(errors.preferences?.favoriteStore) &&
                helperMessages('favoriteStore', errors.preferences)
              }
            >
              {buildOptionsByKey(stores)}
            </Select>
          </Col>
        </Row>

        <Checkbox
          required
          id="loyalty_program"
          name="loyalty_program"
          ref={register({ required: true })}
          value="on"
          onChange={toggleCheckbox}
        >
          <Text size="kilo" noMargin>
            Aceito os termos e quero participar do programa de fidelidade{' '}
            <Text
              as="strong"
              size="kilo"
              bold
              noMargin
              title="Visualizar regulamento do programa Leroy Merlin Com Você"
            >
              <a
                href="/institucional/regulamento-programa-leroy-merlin-com-voce"
                target="_blank"
              >
                Leroy Merlin Com Você.
              </a>
            </Text>
          </Text>
        </Checkbox>

        {isLoyaltyPro && (
          <Stack spacing="byte">
            <Checkbox
              id="loyalty_program_pro"
              name="loyalty_program_pro"
              ref={register({ required: false })}
              value="on"
              onChange={toggleEmployeeLoyalty}
            >
              <Heading size="kilo" noMargin>
                Sou um profissional da construção civil
              </Heading>
            </Checkbox>
            <S.DescriptionOccupations size="mega" noMargin>
              Profissionais como Engenheiros, Arquitetos, Decoradores, Pedreiro,
              etc possuem vantagens e funcionalidades exclusivas como carteira
              de clientes e acesso a eventos.
            </S.DescriptionOccupations>
          </Stack>
        )}

        <Stack spacing="giga">
          {isEmployee && (
            <Select
              id="occupation"
              name="occupation"
              ref={register({ required: true })}
              isInvalid={!!errors.occupation}
              placeholder="Selecionar profissão..."
              validationHint={
                !isEmpty(errors.occupation)
                  ? helperMessages('occupation', errors)
                  : 'Personalizaremos o programa de acordo com a sua profissão'
              }
            >
              {buildOptionsByKey(occupations)}
            </Select>
          )}

          <Button
            type="submit"
            size="kilo"
            isStretch
            isDisabled={!buttonDisabled}
            isLoading={loading}
          >
            Continuar
          </Button>
        </Stack>
      </Stack>
    </form>
  )
}

Form.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  regions: PropTypes.array,
  stores: PropTypes.array,
  isLoyaltyPro: PropTypes.bool
}

export default Form
